/**
 * GUS Admin Portal Design System - Theme Definitions
 * Dark and Light theme configurations for styled-components
 */

import { brandColors, moduleColors, gradients as gradientTokens } from './tokens';

export interface Theme {
  name: 'light' | 'dark';
  colors: {
    primary: string;
    success: string;
    warning: string;
    danger: string;
    background: {
      primary: string;
      secondary: string;
      tertiary: string;
      overlay: string;
      glass: string;
      card: string;
      hover: string;
    };
    text: {
      primary: string;
      secondary: string;
      tertiary: string;
      accent: string;
    };
    border: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
    accent: {
      primary: string;
      success: string;
      warning: string;
      danger: string;
      purple: string;
      info: string;
    };
    sidebar: {
      background: string;
      hover: string;
      active: string;
      text: string;
      textHover: string;
    };
    input: {
      background: string;
      border: string;
      text: string;
      placeholder: string;
      hoverBackground: string;
    };
    button: {
      primary: string;
      primaryHover: string;
      secondary: string;
      secondaryHover: string;
      text: string;
    };
    scrollbar: {
      track: string;
      thumb: string;
      thumbHover: string;
    };
  };
  brandColors: typeof brandColors;
  moduleColors: typeof moduleColors;
  gradients: {
    primary: string;
    success: string;
    accent: string;
    subtlePrimary: string;
    subtleSuccess: string;
  };
  typography: {
    title: {
      size: string;
      weight: string;
      letterSpacing: string;
    };
    subtitle: {
      size: string;
      weight: string;
    };
    body: {
      size: string;
      weight: string;
    };
    small: {
      size: string;
      weight: string;
    };
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  effects: {
    glass: {
      background: string;
      backdropFilter: string;
      border: string;
    };
    shadow: {
      sm: string;
      md: string;
      lg: string;
    };
  };
}

export const darkTheme: Theme = {
  name: 'dark',
  colors: {
    primary: '#32828B',
    success: '#41A35F',
    warning: '#f59e0b',
    danger: '#ef4444',
    background: {
      primary: '#0a0a0a',
      secondary: '#1a1a1a',
      tertiary: '#222',
      overlay: 'rgba(0, 0, 0, 0.5)',
      glass: 'rgba(26, 26, 26, 0.9)',
      card: '#1a1a1a',
      hover: 'rgba(255, 255, 255, 0.1)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#94a3b8',
      tertiary: '#64748b',
      accent: '#32828B',
    },
    border: {
      primary: '#333',
      secondary: '#444',
      tertiary: '#666',
    },
    accent: {
      primary: '#32828B',
      success: '#41A35F',
      warning: '#f59e0b',
      danger: '#ef4444',
      purple: '#8b5cf6',
      info: '#3b82f6',
    },
    sidebar: {
      background: '#1a1a1a',
      hover: 'rgba(50, 130, 139, 0.2)',
      active: '#32828B',
      text: '#9CA3AF',
      textHover: '#6fa7ad',
    },
    input: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: 'rgba(255, 255, 255, 0.1)',
      text: '#ffffff',
      placeholder: '#64748b',
      hoverBackground: 'rgba(26, 26, 26, 0.8)',
    },
    button: {
      primary: '#32828B',
      primaryHover: '#2d757d',
      secondary: 'rgba(255, 255, 255, 0.1)',
      secondaryHover: 'rgba(255, 255, 255, 0.2)',
      text: '#ffffff',
    },
    scrollbar: {
      track: 'rgba(26, 26, 26, 0.3)',
      thumb: 'rgba(148, 163, 184, 0.3)',
      thumbHover: 'rgba(148, 163, 184, 0.5)',
    },
  },
  brandColors,
  moduleColors,
  gradients: {
    primary: gradientTokens.primary,
    success: gradientTokens.success,
    accent: gradientTokens.accent,
    subtlePrimary: gradientTokens.subtlePrimary,
    subtleSuccess: gradientTokens.subtleSuccess,
  },
  typography: {
    title: {
      size: '24px',
      weight: '600',
      letterSpacing: '-0.5px',
    },
    subtitle: {
      size: '15px',
      weight: '500',
    },
    body: {
      size: '14px',
      weight: '400',
    },
    small: {
      size: '12px',
      weight: '400',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  effects: {
    glass: {
      background: 'rgba(10, 10, 10, 0.4)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(148, 163, 184, 0.1)',
    },
    shadow: {
      sm: '0 2px 8px rgba(0, 0, 0, 0.2)',
      md: '0 4px 16px rgba(0, 0, 0, 0.3)',
      lg: '0 8px 32px rgba(0, 0, 0, 0.4)',
    },
  },
};

export const lightTheme: Theme = {
  name: 'light',
  colors: {
    primary: '#32828B',
    success: '#41A35F',
    warning: '#f59e0b',
    danger: '#dc2626',
    background: {
      primary: '#ffffff',
      secondary: '#f8f9fa',
      tertiary: '#e9ecef',
      overlay: 'rgba(0, 0, 0, 0.3)',
      glass: 'rgba(255, 255, 255, 0.9)',
      card: '#ffffff',
      hover: 'rgba(0, 0, 0, 0.05)',
    },
    text: {
      primary: '#0f172a',
      secondary: '#374151',
      tertiary: '#6b7280',
      accent: '#32828B',
    },
    border: {
      primary: 'rgba(0, 0, 0, 0.12)',
      secondary: 'rgba(0, 0, 0, 0.08)',
      tertiary: '#94a3b8',
    },
    accent: {
      primary: '#32828B',
      success: '#41A35F',
      warning: '#f59e0b',
      danger: '#dc2626',
      purple: '#7c3aed',
      info: '#2563eb',
    },
    sidebar: {
      background: '#f8f9fa',
      hover: '#e0eced',
      active: '#32828B',
      text: '#6B7280',
      textHover: '#32828B',
    },
    input: {
      background: '#ffffff',
      border: '#e2e8f0',
      text: '#1e293b',
      placeholder: '#94a3b8',
      hoverBackground: '#f8fafc',
    },
    button: {
      primary: '#32828B',
      primaryHover: '#2d757d',
      secondary: '#e2e8f0',
      secondaryHover: '#cbd5e1',
      text: '#ffffff',
    },
    scrollbar: {
      track: '#f1f5f9',
      thumb: '#cbd5e1',
      thumbHover: '#94a3b8',
    },
  },
  brandColors,
  moduleColors,
  gradients: {
    primary: gradientTokens.primary,
    success: gradientTokens.success,
    accent: gradientTokens.accent,
    subtlePrimary: gradientTokens.subtlePrimary,
    subtleSuccess: gradientTokens.subtleSuccess,
  },
  typography: {
    title: {
      size: '24px',
      weight: '600',
      letterSpacing: '-0.5px',
    },
    subtitle: {
      size: '15px',
      weight: '500',
    },
    body: {
      size: '14px',
      weight: '400',
    },
    small: {
      size: '12px',
      weight: '400',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  effects: {
    glass: {
      background: 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(226, 232, 240, 0.5)',
    },
    shadow: {
      sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
      md: '0 4px 6px rgba(0, 0, 0, 0.1)',
      lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    },
  },
};

// Helper function to get theme by name
export const getTheme = (themeName: 'light' | 'dark'): Theme => {
  return themeName === 'light' ? lightTheme : darkTheme;
};

const themes = { darkTheme, lightTheme, getTheme };
export default themes;
